import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectorRef, AfterViewInit, ElementRef } from '@angular/core';
import { Location } from '@angular/common'
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormBuilder, Validators, ControlContainer, FormGroupDirective, UntypedFormGroup, FormControl, FormGroup } from '@angular/forms';
import { SystemService } from '../../services/system.service'
import { GlobalSearchService } from '../../services/globalsearchservice.service'
import { GlobalsService } from '../../services/globals.service'
@Component({
	selector: 'app-mail',
	templateUrl: './mail.component.html',
	styleUrls: ['./mail.component.scss']
})
/**
 * Outgoing mail (SMTP) setup: how the ERP authenticates when it sends. One sending method is
 * active at a time — legacy username/password, or a mailbox signed in through Microsoft or Google
 * OAuth2. OAuth2 replaces only the password; the host/port/mailbox connection below is shared.
 */
export class MailComponent implements OnInit {
	emailForm: UntypedFormGroup;
	vendorDiscForm: FormGroup;
	color = 'blue';

	/** Prerequisite checklist from apir/globals/settings/mail_oauth_status — null until loaded. */
	oauthStatus: any = null;
	oauthStatusLoading = false;
	/** True when the status route did not answer (server not yet carrying it) — never block on it. */
	oauthStatusUnavailable = false;
	/** Sticky reason the last sign-in attempt was refused; a toast alone is too easy to miss. */
	oauthError = '';
	signingIn = false;
	redirectCopied = false;

	private readonly connectionDefaults: { [provider: string]: { host: string, port: string } } = {
		oauth_microsoft: { host: 'smtp.office365.com', port: '587' },
		oauth_google: { host: 'ssl://smtp.gmail.com', port: '465' },
	};
	constructor(
		private globalSearchService: GlobalSearchService,
		private systemService: SystemService,
		private globalsService: GlobalsService,
		private location: Location,
		private fb: UntypedFormBuilder,
		private route: ActivatedRoute,
		private router: Router,
	) {
		this.color = this.globalSearchService.getColor();
	}

	ngOnInit(): void {
		this.vendorDiscForm = this.fb.group({
			enabled: [false],
			recipients: [''],
			notifyInvoiceVsPo: [false],
			notifyPpv: [true],
			notifyPqv: [true],
		});
		this.loadVendorDiscForm();

		this.route.queryParams.subscribe((params) => {
			if (params['oauth_ok']) {
				this.globalSearchService.showNotification('Mailbox authorization saved.', 'success', 'bottom', 'right');
				this.router.navigate([], { relativeTo: this.route, queryParams: {}, replaceUrl: true });
				this.loadForm();
			} else if (params['oauth_error']) {
				this.globalSearchService.showNotification('Mailbox authorization failed or was cancelled.', 'warning', 'bottom', 'right');
				this.router.navigate([], { relativeTo: this.route, queryParams: {}, replaceUrl: true });
			}
		});

		this.emailForm = this.fb.group({
			config_mail_parameter: [],
			config_mail_smtp_hostname: [],
			config_mail_smtp_username: [],
			config_mail_smtp_password: [],
			config_mail_smtp_port: [],
			config_mail_smtp_timeout: [],
			config_mail_engine: [],
			config_mail_alert_email: [],
			config_mail_smtp_auth: ['login'],
			config_mail_oauth_tenant_id: [],
			config_mail_oauth_client_id: [],
			config_mail_oauth_refresh_token: [],
		});

		this.loadForm();

	}

	loadVendorDiscForm() {
		this.globalsService.getVendorInvoiceDiscrepancyNotify().subscribe((r: any) => {
			if (!r) { return; }
			this.vendorDiscForm.patchValue({
				enabled: !!r.enabled,
				recipients: r.recipients || '',
				notifyInvoiceVsPo: !!r.notifyInvoiceVsPo,
				notifyPpv: r.notifyPpv !== false,
				notifyPqv: r.notifyPqv !== false,
			});
		});
	}

	saveVendorDiscSettings() {
		const v = this.vendorDiscForm.value;
		this.globalsService.saveVendorInvoiceDiscrepancyNotify(v).subscribe((res: any) => {
			if (res && res.success) {
				this.globalSearchService.showNotification('Vendor invoice discrepancy settings saved.', 'success', 'bottom', 'right');
			} else {
				this.globalSearchService.showNotification('Could not save settings.', 'warning', 'bottom', 'right');
			}
		});
	}

	loadForm() {
		this.systemService.getEmailSettings().subscribe( (result: any) => {

			this.emailForm = this.fb.group({
				config_mail_parameter: [result.config_mail_parameter],
				config_mail_smtp_hostname: [result.config_mail_smtp_hostname],
				config_mail_smtp_username: [result.config_mail_smtp_username],
				config_mail_smtp_password: [result.config_mail_smtp_password],
				config_mail_smtp_port: [result.config_mail_smtp_port],
				config_mail_smtp_timeout: [result.config_mail_smtp_timeout],
				config_mail_alert_email:  [result.config_mail_alert_email],
				config_mail_engine: [result.config_mail_engine],
				config_mail_smtp_auth: [result.config_mail_smtp_auth || 'login'],
				config_mail_oauth_tenant_id: [result.config_mail_oauth_tenant_id],
				config_mail_oauth_client_id: [result.config_mail_oauth_client_id],
				config_mail_oauth_refresh_token: [result.config_mail_oauth_refresh_token],
			});
			this.loadOAuthStatus();
		});
	}

	/**
	 * Asks the server which prerequisites are actually satisfied (client id saved, server-side
	 * client secret present, key file readable, token stored) so the screen can show pass/fail
	 * rather than telling the operator to go and check four things by hand.
	 */
	loadOAuthStatus(): void {
		if (!this.isOauth) {
			this.oauthStatus = null;
			return;
		}
		this.oauthStatusLoading = true;
		this.systemService.getMailOAuthStatus(this.smtpAuth).subscribe(
			(res: any) => {
				this.oauthStatusLoading = false;
				this.oauthStatus = (res && res.success) ? res : null;
				this.oauthStatusUnavailable = !this.oauthStatus;
			},
			() => {
				this.oauthStatusLoading = false;
				this.oauthStatus = null;
				this.oauthStatusUnavailable = true;
			},
		);
	}

	/**
	 * Switching sending method fills in that provider's relay host/port, but only when the current
	 * value is blank or is the other provider's default — a hand-tuned relay is left alone.
	 */
	onAuthMethodChange(): void {
		this.oauthError = '';
		const d = this.connectionDefaults[this.smtpAuth];
		if (d) {
			const host = (this.emailForm.get('config_mail_smtp_hostname')?.value || '').toString().trim();
			const port = (this.emailForm.get('config_mail_smtp_port')?.value || '').toString().trim();
			// Compare bare hostnames: the relay may carry a tls:// or ssl:// prefix that the server
			// adds for STARTTLS, and tls://smtp.office365.com is still the Microsoft default.
			const bare = (h: string) => h.replace(/^(tls|ssl):\/\//i, '');
			const isOtherDefault = Object.keys(this.connectionDefaults)
				.filter((k) => k !== this.smtpAuth)
				.some((k) => bare(this.connectionDefaults[k].host) === bare(host));
			if (host === '' || isOtherDefault) {
				this.emailForm.patchValue({ config_mail_smtp_hostname: d.host });
			}
			if (port === '' || isOtherDefault) {
				this.emailForm.patchValue({ config_mail_smtp_port: d.port });
			}
		}
		this.loadOAuthStatus();
	}

	/**
	 * Every prerequisite the server can check before it will build an authorize URL. When the
	 * status route itself is unreachable (an API not yet carrying it) this stays true, so the
	 * button behaves as it did before and the server's own refusal message is what surfaces —
	 * a missing checklist must never be the thing that blocks sign-in.
	 */
	get prereqsOk(): boolean {
		const s = this.oauthStatus;
		if (!s) { return this.oauthStatusUnavailable; }
		return !!s.client_id_saved && !!s.client_secret_set && !!s.keyfile_ok && !!s.redirect_uri
			&& (!s.tenant_required || !!s.tenant_saved);
	}

	get providerLabel(): string {
		return this.isGoogleOauth ? 'Google' : 'Microsoft';
	}

	copyRedirectUri(): void {
		const uri = this.oauthStatus?.redirect_uri || '';
		if (!uri || !navigator.clipboard) { return; }
		navigator.clipboard.writeText(uri).then(() => {
			this.redirectCopied = true;
			setTimeout(() => { this.redirectCopied = false; }, 2000);
		});
	}

	get smtpAuth(): string {
		return this.emailForm?.get('config_mail_smtp_auth')?.value || 'login';
	}

	get isOauth(): boolean {
		return this.smtpAuth.indexOf('oauth') === 0;
	}

	get isGoogleOauth(): boolean {
		return this.smtpAuth === 'oauth_google';
	}

	get isMicrosoftOauth(): boolean {
		return this.smtpAuth === 'oauth_microsoft';
	}

	/**
	 * Sends the operator to the provider consent screen. The saved settings must already hold the
	 * client id (and tenant, for Microsoft) — the server refuses to build the URL otherwise.
	 */
	startOAuth(provider: string): void {
		const label = provider === 'oauth_google' ? 'Google' : 'Microsoft';
		const returnUrl = `${window.location.origin}${window.location.pathname}#/system/email`;
		this.oauthError = '';
		this.signingIn = true;
		this.systemService.beginMailOAuth(returnUrl, provider).subscribe(
			(res: any) => {
				if (res && res.success && res.authorize_url) {
					window.location.href = res.authorize_url;
					return;
				}
				this.signingIn = false;
				// The server refuses before redirecting when a prerequisite is missing, and its
				// message names the missing one — keep it on screen, not just in a toast.
				this.oauthError = (res && res.message) ? res.message : `Could not start ${label} sign-in.`;
				this.globalSearchService.showNotification(this.oauthError, 'warning', 'bottom', 'right');
				this.loadOAuthStatus();
			},
			() => {
				this.signingIn = false;
				this.oauthError = `Could not reach the server to start ${label} sign-in.`;
				this.globalSearchService.showNotification(this.oauthError, 'warning', 'bottom', 'right');
			},
		);
	}

	saveEmailSettings() {
		if(this.emailForm.valid) {
			this.systemService.saveEmailSettings(this.emailForm.value).subscribe(result => {
				this.loadForm();
				this.globalSearchService.showNotification('Updated', 'success', 'bottom', 'right');
			});
		}
	}

	back(): void {
		this.location.back()
	}

	update() {

	}

}
